import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PaginatedUserResponse } from '../components/user/model/paginated-user.model';
import { GroupFilters } from '../components/user/model/group-filters.model';
import { PaginatedGroupResponse } from '../components/user/model/paginated-group.model';
import { UserFilters } from '../components/user/model/user-filters.model';
import { Group } from '../components/user/model/group';
import { UserFunction } from '../components/user-group/model/UserFunction';
import { PaginatedAssignedCustomerResponse } from '../components/select-customer/model/paginated-assigned-customer.model';
import { UserGroupModel } from '../components/user-group/model/user-group.model';
import { UserState } from '../../../shared/state/UserState';
import { Store } from '@ngxs/store';
import { ADMIN_GROUP_NAME } from '../../../shared/constants/constants';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  public http = inject(HttpClient);
  public store = inject(Store);

  getUsers(dealerId: string, filters: UserFilters, pageNumber: number, pageSize: number): Observable<PaginatedUserResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getUsers.replace('{dealerId}', dealerId)}`;

    // Remove null values from filters
    const filteredFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null) {
        acc[key] = value;
      }
      return acc;
    }, {} as UserFilters);

    return this.http.get<PaginatedUserResponse>(url, {
      params: { ...filteredFilters, page: pageNumber, pageSize: pageSize, userId: this.store.selectSnapshot(UserState.getUserName) }
    });
  }

  getGroups(dealerId: string, filters: GroupFilters, pageNumber: number, pageSize: number): Observable<PaginatedGroupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getGroups.replace('{dealerId}', dealerId)}`;

    // Remove null values from filters
    const filteredFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null) {
        acc[key] = value;
      }
      return acc;
    }, {} as GroupFilters);

    return this.http.get<PaginatedGroupResponse>(url, {
      params: { ...filteredFilters, page: pageNumber, pageSize }
    });
  }

  unlockAccount(dealerId: string, userIdentifier: { username: string }): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.unlockAccount.replace('{dealerId}', dealerId)}`;
    return this.http.put<any>(url, userIdentifier);
  }

  resetPassword(dealerId: string, username: string, password: string): Observable<any> {
    const requestBody = { username, password };
    const url = `${environment.backOfficeUrl}${environment.serviceUris.resetPassword.replace('{dealerId}', dealerId)}`;
    return this.http.put<any>(url, requestBody);
  }

  updateUser(dealerId: string, username: string, user: any): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateUser.replace('{dealerId}', dealerId)}`;
    const headers = { username };
    return this.http.put<any>(url, user, { headers });
  }

  addUser(dealerId: string, username: string, password: string, user: any): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.addUser.replace('{dealerId}', dealerId)}`;
    const headers = { username, password };
    return this.http.post<any>(url, user, { headers });
  }

  deleteGroup(dealerId: string, userGroupId: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteGroup.replace('{dealerId}', dealerId)}`;
    const headers = { userGroupId };
    return this.http.delete<any>(url, { headers });
  }

  getUserGroups(dealerId: string): Observable<Group[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getUserGroups.replace('{dealerId}', dealerId)}`;
    return this.http
      .get<{
        data: Group[];
      }>(url, { headers: { dealerId } })
      .pipe(map((response) => response.data.filter((group) => group.groupName?.toUpperCase() !== ADMIN_GROUP_NAME)));
  }

  getUserGroupDetails(dealerId: string, groupId: string): Observable<UserFunction> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getUserGroupDetails.replace('{dealerId}', dealerId)}/${groupId}`;
    return this.http
      .get<{
        data: UserFunction;
      }>(url, { headers: { dealer: dealerId } })
      .pipe(map((response) => response.data));
  }

  searchUserAssignToCustomer(
    dealerId: string,
    searchString: string,
    customerType: string,
    assignmentStatus: string,
    userId: string,
    plantId: string,
    pageNumber: number,
    pageSize: number
  ): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchUserAssignToCustomer}`;

    const headers = { dealerId, userId, plantId };
    return this.http.get<PaginatedAssignedCustomerResponse>(url, {
      headers,
      params: {
        searchString,
        customerType,
        assignmentStatus,
        page: pageNumber,
        pageSize
      }
    });
  }

  updateUserGroup(dealerId: string, userGroupUpdateDTO: UserGroupModel): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateUserGroup.replace('{dealerId}', dealerId)}`;
    return this.http.put<any>(url, userGroupUpdateDTO);
  }

  saveUserGroup(dealerId: string, userGroupDTO: UserGroupModel): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.saveUserGroup}`;
    const headers = { dealerId };
    return this.http.post<{
      data: UserGroupModel;
    }>(url, userGroupDTO, { headers });
  }

  legacyToken(dealerId: string): Observable<any> {
    const url = `${environment.authUrl}${environment.serviceUris.authBase}${dealerId}/legacyToken`;
    return this.http.post(url, {}, { observe: 'response' }).pipe(map((response) => response));
  }
}
